import { Vector2 } from '@babylonjs/core';
import Element, { ElementType, SpecyficType } from './element';
import IShape, { BaseShape } from './IShape';
import { elements } from './elementMenuItem';

class PlacementManager {

    private _elements: Element[] = [];

    /**
     * Procedura ustawiająca listę elementów niezbędą do obliczeń.
     * @param elements Lista elementów na scenie.
     */
    public initialize(elements: Element[]) {
        this._elements = elements;
    }

    /**
     * Procedura decydująca czy element o podanym kształcie można umieścić w danym miejscu.
     * @param position Pozycja, na której chcemy umieścić element.
     * @param shape Kształt umieszczanego elementu.
     * @param type Typ umieszczanego elementu (np. element wewnętrzny).
     * @param specyficType Typ konkretny (np. szuflada).
     */
    public canPlace(
        position: Vector2,
        shape: Vector2,
        type: ElementType,
        specyficType: SpecyficType = SpecyficType.OTHER,
        without: (x: Element) => boolean = () => false): boolean {
        const elements = this.getElementsToTest(type, specyficType)
            .filter((x) => !without(x));
        const testingShape = new BaseShape(position, shape);
        const isAllowed = this.isAllowed(testingShape, type, specyficType);
        if (!isAllowed) { return false; }
        return !elements.some((x) => this.test(testingShape, type, specyficType, x));
    }

    private getElementsToTest(type: ElementType, specyficType: SpecyficType): Element[] {
        const result = this._elements.filter((x) => x.type === type);
        if (type === ElementType.INSIDE) {
            result.push(...this._elements.filter((x) => x.type === ElementType.WALL));
            result.push(...this._elements.filter((x) => x.type === ElementType.FRONT));
        }
        if (type === ElementType.WALL) {
            result.push(...this._elements.filter((x) => x.type === ElementType.INSIDE));
        }
        if (type === ElementType.FRONT) {
            result.push(...this._elements.filter((x) =>
                x.type === ElementType.WALL ||
                x.specyficType === SpecyficType.HANGER ||
                x.specyficType === SpecyficType.SHELF,
            ));
        }
        return result;
    }

    private isAllowed(testingShape: IShape, type: ElementType, specyficType: SpecyficType): boolean {
        if (specyficType === SpecyficType.DRAWER && testingShape.position.y > 2) {
            // ograniczenie wysokościowe szuflad
            return false;
        }
        return true;
    }

    private test(testingShape: IShape, type: ElementType, specyficType: SpecyficType, x: Element): boolean {
        if (type === ElementType.WALL &&
            x.type === ElementType.WALL) { // kolidowanie ze sobą przegród
            return testingShape.collide(x) || testingShape.inside(x);
        }
        if (type === ElementType.FRONT &&
            x.specyficType === SpecyficType.HANGER &&
            x.inside(testingShape)) { // nakładanie na wieszaki niemniejszych frontów
            return testingShape.smaller(x);
        }
        if (type === ElementType.FRONT &&
            x.specyficType === SpecyficType.SHELF &&
            testingShape.collide(x)) { // nakładanie na półki niemniejszych frontów
            return testingShape.smaller(x);
        }
        if (type === ElementType.INSIDE &&
            specyficType !== SpecyficType.DRAWER &&
            x.type === ElementType.FRONT &&
            testingShape.inside(x.slot)) { // wkładanie pod front elementów nietworzących przegród pionowych
            return x.slot.shape.x !== testingShape.shape.x;
        }
        if (specyficType === SpecyficType.SHELF &&
            x.specyficType === SpecyficType.DRAWER) {
            return testingShape.collide(x) || (
                (testingShape.position.y === x.position.y - x.shape.y / 2 &&
                    testingShape.position.x === x.position.x) ||
                (testingShape.position.y === x.position.y + x.shape.y / 2 &&
                    testingShape.position.x === x.position.x)
            );
        }
        if (specyficType === SpecyficType.SHELF &&
            x.type === ElementType.WALL
        ) { // nakładanie półki na przegrodę.
            return testingShape.collide(x) || testingShape.inside(x);
        }
        if (specyficType === SpecyficType.SHELF &&
            x.specyficType === SpecyficType.HANGER &&
            x.shape.x === 2 &&
            testingShape.shape.x === 1) {
            const upperTest = new BaseShape(
                new Vector2(testingShape.position.x, testingShape.position.y + 0.25),
                Vector2.Zero(),
            );
            const lowerTest = new BaseShape(
                new Vector2(testingShape.position.x, testingShape.position.y - 0.25),
                Vector2.Zero(),
            );
            if (upperTest.inside(x) || lowerTest.inside(x)) {
                return true;
            }
        }
        if (specyficType === SpecyficType.SHELF &&
            x.specyficType === SpecyficType.SHELF) {
            if (x.shape.x === 2 &&
                Number.isInteger(x.position.y) &&
                testingShape.shape.x === 1) {
                // półka krótka w sąsiedztwie półki długiej.
                const upperTest = new BaseShape(
                    new Vector2(testingShape.position.x, testingShape.position.y + 0.5),
                    Vector2.Zero(),
                );
                const lowerTest = new BaseShape(
                    new Vector2(testingShape.position.x, testingShape.position.y - 0.5),
                    Vector2.Zero(),
                );
                if (upperTest.inside(x) || lowerTest.inside(x)) {
                    return true;
                }
            }
            // nakładanie półki na półkę.
            return testingShape.collide(x) || testingShape.inside(x);
        }
        return testingShape.collide(x);
    }
}

export const placementManager = new PlacementManager();
